import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import ToastMe from '@/components/ui/ToastMe';
import SystemService from '@/services/SystemService';

const LICENSE_GATE_STORAGE_KEY = 'license_gate_state';
const TABAN_WEBSITE_URL = (
  process.env.REACT_APP_TABAN_WEBSITE_URL || 'https://taban.so'
).replace(/\/+$/, '');

const BRAND = {
  background: '#f4f6fb',
  primary: '#156372',
  accent: '#FFC107',
};

function readStoredGate() {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(LICENSE_GATE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearStoredGate() {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(LICENSE_GATE_STORAGE_KEY);
  } catch {}
}

function formatDateTime(value) {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString();
}

function isLicenseActive(snapshot) {
  return Boolean(snapshot?.status === 'ok' && snapshot?.cacheValid && !snapshot?.locked);
}

function resolveTitle(snapshot, gate) {
  const status = String(snapshot?.licensing?.status || gate?.status || '').toLowerCase();

  if (status === 'expired' || status === 'license_expired') {
    return snapshot?.licensing?.isTrial ? 'Free Trial Ended' : 'License Expired';
  }

  if (status === 'missing' || status === 'license_required') {
    return 'License Required';
  }

  return 'License Attention Needed';
}

const DetailCard = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
      {label}
    </p>
    <p className="mt-2 break-words text-sm text-slate-900">{value || '—'}</p>
  </div>
);

const LicenseStatusPage = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [storedGate, setStoredGate] = useState(() => readStoredGate());
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await SystemService.fetchLicenseStatus();
      setSnapshot(data);

      if (isLicenseActive(data)) {
        clearStoredGate();
        setStoredGate(null);
      }
    } catch (error) {
      ToastMe(
        error?.response?.data?.message ||
          error?.message ||
          'Unable to load license status right now.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const active = useMemo(() => isLicenseActive(snapshot), [snapshot]);

  const title = useMemo(
    () => resolveTitle(snapshot, storedGate),
    [snapshot, storedGate]
  );

  const primaryMessage = useMemo(() => {
    if (active) {
      return 'Your access is active again. You can continue to sign in or finish workspace setup.';
    }

    return (
      snapshot?.licensing?.message ||
      storedGate?.message ||
      'Your workspace needs a valid Taban Foods license before you can continue.'
    );
  }, [active, snapshot, storedGate]);

  const renewLabel = useMemo(() => {
    const status = String(snapshot?.licensing?.status || storedGate?.status || '').toLowerCase();
    if (status === 'missing' || status === 'license_required') return 'Register Access Key';
    return 'Update Access Key';
  }, [snapshot, storedGate]);

  return (
    <section
      className="min-h-screen px-4 py-10"
      style={{ backgroundColor: BRAND.background }}
    >
      <div className="mx-auto w-full max-w-5xl">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
          <div
            className="px-6 py-8 text-white md:px-10 md:py-10"
            style={{
              background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.accent} 100%)`,
            }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/75">
              Taban Foods Access
            </p>
            <h1 className="mt-3 text-3xl font-semibold text-white">{title}</h1>
            <p className="mt-3 max-w-3xl text-sm text-white/90">{primaryMessage}</p>
          </div>

          <div className="px-6 py-8 md:px-10 md:py-10">
            {loading && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                Refreshing license status...
              </div>
            )}

            {!loading && (
              <>
                <div
                  className={`rounded-2xl border px-4 py-4 text-sm ${
                    active
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : 'border-amber-200 bg-amber-50 text-amber-800'
                  }`}
                >
                  {active
                    ? 'License check passed.'
                    : 'The application blocked access because the current license state is not active.'}
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <DetailCard
                    label="Status"
                    value={snapshot?.licensing?.status || storedGate?.status || '—'}
                  />
                  <DetailCard
                    label="Product"
                    value={snapshot?.licensing?.productName || 'Taban Foods'}
                  />
                  <DetailCard
                    label="Plan"
                    value={snapshot?.licensing?.plan || '—'}
                  />
                  <DetailCard
                    label="Access Type"
                    value={snapshot?.licensing?.accessType || '—'}
                  />
                  <DetailCard
                    label="License Key"
                    value={snapshot?.licensing?.keyHint || 'Stored privately'}
                  />
                  <DetailCard
                    label="License Expires"
                    value={formatDateTime(snapshot?.licensing?.expiresAt)}
                  />
                  <DetailCard
                    label="Trial Ends"
                    value={formatDateTime(snapshot?.licensing?.trialEndsAt)}
                  />
                  <DetailCard
                    label="Trial Cleanup"
                    value={formatDateTime(snapshot?.licensing?.trialCleanupAt)}
                  />
                  <DetailCard
                    label="Last Blocked"
                    value={formatDateTime(storedGate?.occurredAt)}
                  />
                </div>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <button
                    type="button"
                    onClick={loadStatus}
                    className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold shadow-sm"
                    style={{ backgroundColor: BRAND.primary, color: '#fff' }}
                  >
                    Refresh Status
                  </button>

                  <Link
                    to="/sign-in"
                    className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    {active ? 'Continue to Sign In' : renewLabel}
                  </Link>

                  {active && (
                    <Link
                      to="/setup"
                      className="inline-flex items-center justify-center rounded-xl border border-emerald-300 px-5 py-3 text-sm font-semibold text-emerald-800 hover:bg-emerald-50"
                    >
                      Continue Setup
                    </Link>
                  )}

                  <a
                    href={TABAN_WEBSITE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-xl border border-amber-300 px-5 py-3 text-sm font-semibold text-amber-900 hover:bg-amber-50"
                  >
                    Choose a Package
                  </a>
                </div>

                <p className="mt-5 text-sm text-slate-600">
                  If you already renewed on the Taban website, enter the renewed access key on the sign-in
                  screen, then come back here and refresh the status.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LicenseStatusPage;
